<script setup>
import AppHeader from './components/AppHeader.vue'
import { SignedIn, useSession } from '@clerk/vue';
import { ref } from 'vue';
import SignIn from '@/views/SignIn.vue';

const { session, isLoaded } = useSession();
const isUserLogged = ref(false);

async function clerkAuth() {
  if(isLoaded.value) {
    if(session.value) {
      const token = await session.value.getToken();
      localStorage.setItem('token', token);
      isUserLogged.value = true;
    } else {
      localStorage.removeItem('token');
      isUserLogged.value = false;
    }
  }
}

setInterval(clerkAuth, 5000);
setTimeout(clerkAuth, 1000);
clerkAuth();
</script>

<template>
  <notifications />
  <div class="container">
    <SignedIn>
      <AppHeader/>
    </SignedIn>

    <div v-if="isUserLogged" style="margin-top: 24px; margin-bottom: 24px; padding: 0 8px;">
      <router-view/>
    </div>
    <div class="auth-pending" v-else>
      <SignIn path="/sign-in" routing="path" />
    </div>
  </div>
</template>

<style>
:root {
  --admin-primary: #2563eb;
  --admin-primary-light: #dbeafe;
  --admin-primary-hover: #1d4ed8;
  --admin-bg: #f1f5f9;
  --admin-card-bg: #ffffff;
  --admin-text: #0f172a;
  --admin-text-secondary: #334155;
  --admin-text-muted: #64748b;
  --admin-border: #e2e8f0;
  --admin-border-light: #f1f5f9;
  --admin-radius: 12px;
  --admin-radius-sm: 8px;
  --admin-shadow-xs: 0 1px 2px rgba(0, 0, 0, 0.04);
  --admin-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.03);
  --admin-shadow-md: 0 4px 16px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04);
  --admin-shadow-lg: 0 10px 40px rgba(0, 0, 0, 0.1);
  --admin-transition: 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

* {
  box-sizing: border-box;
}

body {
  background-color: var(--admin-bg) !important;
  margin: 0;
}

#app {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: var(--admin-text);
  font-size: 0.925rem;
  line-height: 1.6;
}

nav {
  padding: 0;
}

nav a {
  font-weight: 500;
  color: var(--admin-text);
}

nav a.router-link-exact-active {
  color: var(--admin-primary) !important;
}

.auth-pending {
  margin: 20px;
  font-size: 20px;
}

/* ── Buttons ── */
.btn {
  font-size: 0.875rem !important;
  letter-spacing: 0.01em;
}

.btn-primary {
  background-color: var(--admin-primary) !important;
  border-color: var(--admin-primary) !important;
  border-radius: var(--admin-radius-sm) !important;
  font-weight: 600 !important;
  padding: 0.5rem 1.25rem !important;
  transition: all var(--admin-transition) !important;
}

.btn-primary:hover,
.btn-primary:focus {
  background-color: var(--admin-primary-hover) !important;
  border-color: var(--admin-primary-hover) !important;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.35) !important;
  transform: translateY(-1px);
}

.btn-primary:active {
  transform: translateY(0) !important;
  box-shadow: 0 1px 4px rgba(37, 99, 235, 0.3) !important;
}

.btn-outline-dark {
  border-radius: var(--admin-radius-sm) !important;
  font-weight: 600 !important;
  padding: 0.5rem 1.5rem !important;
  transition: all var(--admin-transition) !important;
  border-color: var(--admin-border) !important;
  color: var(--admin-text-secondary) !important;
}

.btn-outline-dark:hover {
  background-color: var(--admin-text) !important;
  border-color: var(--admin-text) !important;
  color: #fff !important;
  transform: translateY(-1px);
  box-shadow: var(--admin-shadow-md) !important;
}

/* ── Cards ── */
.card {
  border: 1px solid var(--admin-border) !important;
  border-radius: var(--admin-radius) !important;
  box-shadow: var(--admin-shadow-xs) !important;
  transition: box-shadow var(--admin-transition), transform var(--admin-transition) !important;
  overflow: hidden;
  background: var(--admin-card-bg) !important;
}

.card:hover {
  box-shadow: var(--admin-shadow-md) !important;
  transform: translateY(-3px);
}

/* ── Forms ── */
.form-control {
  border-radius: var(--admin-radius-sm) !important;
  border: 1.5px solid var(--admin-border) !important;
  padding: 0.55rem 0.85rem !important;
  font-size: 0.875rem !important;
  color: var(--admin-text) !important;
  background: #fff !important;
  transition: border-color var(--admin-transition), box-shadow var(--admin-transition) !important;
}

.form-control:focus {
  border-color: var(--admin-primary) !important;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12) !important;
}

.form-control::placeholder {
  color: #94a3b8 !important;
}

/* ── Alerts ── */
.alert-info {
  background-color: #f0f9ff !important;
  border: 1px solid #bae6fd !important;
  color: #0369a1 !important;
  border-radius: var(--admin-radius) !important;
  font-weight: 500;
  padding: 1rem 1.25rem !important;
}

/* ── Tables ── */
.table {
  font-size: 0.875rem;
  color: var(--admin-text-secondary);
}

.table > :not(caption) > * > * {
  padding: 0.65rem 1rem;
}

.table-striped > tbody > tr:nth-of-type(odd) > * {
  background-color: var(--admin-border-light) !important;
}

.table-striped > tbody > tr {
  transition: background-color 0.15s ease;
}

/* ── Accordion ── */
.accordion-item {
  border-color: var(--admin-border) !important;
}

.accordion-button {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--admin-text-secondary);
  padding: 0.85rem 1.25rem;
}

.accordion-button:not(.collapsed) {
  background-color: var(--admin-primary-light) !important;
  color: var(--admin-primary) !important;
  box-shadow: none !important;
}

.accordion-button:focus {
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12) !important;
}

.accordion-button::after {
  transition: transform 0.25s ease;
}

/* ── vs-vue3-select ── */
.vs__dropdown-toggle {
  border-radius: var(--admin-radius-sm) !important;
  border: 1.5px solid var(--admin-border) !important;
  padding: 3px 8px !important;
  min-height: 38px !important;
  background: #fff !important;
  transition: border-color var(--admin-transition), box-shadow var(--admin-transition) !important;
}

.vs__dropdown-toggle:focus-within {
  border-color: var(--admin-primary) !important;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12) !important;
}

.vs__search::placeholder {
  color: #94a3b8 !important;
}

.vs__dropdown-menu {
  border-radius: var(--admin-radius-sm) !important;
  border: 1px solid var(--admin-border) !important;
  box-shadow: var(--admin-shadow-lg) !important;
  padding: 4px !important;
  margin-top: 4px !important;
}

.vs__dropdown-option {
  border-radius: 6px !important;
  padding: 6px 10px !important;
  font-size: 0.875rem !important;
}

.vs__dropdown-option--highlight {
  background: var(--admin-primary-light) !important;
  color: var(--admin-primary) !important;
}

/* ── Notifications ── */
.vue-notification-group {
  z-index: 10000 !important;
}

.vue-notification {
  border-radius: var(--admin-radius-sm) !important;
  box-shadow: var(--admin-shadow-md) !important;
  font-family: 'Inter', sans-serif !important;
  font-size: 0.875rem !important;
  padding: 12px 16px !important;
  margin: 8px !important;
  border-left-width: 4px !important;
}

/* ── Scrollbar ── */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* ── Selection ── */
::selection {
  background: var(--admin-primary-light);
  color: var(--admin-primary);
}
</style>
